/**
 * Super Admin — Passkeys / Clés d'accès
 * ---------------------------------------------------------------------
 * Zone sécurité du super_admin (route /dashboard/parametres/securite).
 * Réutilise strictement les endpoints existants :
 *   GET  /api/admin/super-admin/webauthn/credentials
 *   POST /api/admin/super-admin/webauthn/register/begin
 *   POST /api/admin/super-admin/webauthn/register/finish
 *
 * Aucun endpoint de révocation exposé ici — l'ancien credential Emergent
 * (« PC Gilles - Windows Hello ») doit rester intact.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  getSuperAdminPasskeys,
  beginSuperAdminPasskeyRegistration,
  finishSuperAdminPasskeyRegistration,
} from "../lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, ShieldOff, Plus } from "lucide-react";

// ----- base64url ⇄ ArrayBuffer helpers (WebAuthn wire format) -----
function b64urlToBuffer(s) {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}
function bufferToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let str = "";
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function normalizeCreationOptions(raw) {
  const opts = typeof raw === "string" ? JSON.parse(raw) : raw;
  const publicKey = { ...opts };
  publicKey.challenge = b64urlToBuffer(opts.challenge);
  publicKey.user = { ...opts.user, id: b64urlToBuffer(opts.user.id) };
  if (Array.isArray(opts.excludeCredentials)) {
    publicKey.excludeCredentials = opts.excludeCredentials.map((c) => ({
      ...c,
      id: b64urlToBuffer(c.id),
    }));
  }
  return publicKey;
}

function serializePublicKeyCredential(cred) {
  const attResp = cred.response;
  const payload = {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufferToB64url(attResp.attestationObject),
      clientDataJSON: bufferToB64url(attResp.clientDataJSON),
    },
    clientExtensionResults:
      typeof cred.getClientExtensionResults === "function"
        ? cred.getClientExtensionResults()
        : {},
  };
  if (cred.authenticatorAttachment) payload.authenticatorAttachment = cred.authenticatorAttachment;
  if (typeof attResp.getTransports === "function") {
    try {
      const t = attResp.getTransports();
      if (Array.isArray(t) && t.length) payload.response.transports = t;
    } catch (_) {}
  }
  return payload;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
}

export default function SuperAdminPasskeysPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSuperAdminPasskeys();
      setCreds(res.data?.credentials || []);
    } catch (e) {
      toast.error(t("security.passkeys.errors.loadFailed", "Impossible de charger la liste des passkeys."));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRegister = async () => {
    if (registering) return;
    if (!window?.PublicKeyCredential || !navigator?.credentials?.create) {
      toast.error(t("security.passkeys.errors.unsupported", "WebAuthn n'est pas disponible sur ce navigateur."));
      return;
    }
    const defaultName = `PC ${user?.name || "Super admin"} - Windows Hello - itercatus.com`;
    const friendlyName = window.prompt(
      t("security.passkeys.prompt.name", "Nom lisible pour cette passkey (visible dans la liste) :"),
      defaultName,
    );
    if (friendlyName == null) return; // cancelled
    const name = String(friendlyName).trim() || defaultName;

    setRegistering(true);
    try {
      const beginRes = await beginSuperAdminPasskeyRegistration({ friendly_name: name });
      if (!beginRes.data?.ok) {
        throw new Error(beginRes.data?.error || "begin_failed");
      }
      const publicKey = normalizeCreationOptions(beginRes.data.options);
      let cred;
      try {
        cred = await navigator.credentials.create({ publicKey });
      } catch (err) {
        if (err?.name === "NotAllowedError") {
          toast.info(t("security.passkeys.errors.cancelled", "Enregistrement annulé."));
        } else if (err?.name === "InvalidStateError") {
          toast.error(
            t(
              "security.passkeys.errors.alreadyRegistered",
              "Cet appareil possède déjà une passkey enregistrée pour ce compte.",
            ),
          );
        } else {
          toast.error(
            t("security.passkeys.errors.browser", "Erreur navigateur : ") + (err?.message || err?.name || String(err)),
          );
        }
        return;
      }
      if (!cred) {
        toast.error(t("security.passkeys.errors.emptyResponse", "Réponse WebAuthn vide."));
        return;
      }
      const finishRes = await finishSuperAdminPasskeyRegistration({
        friendly_name: name,
        credential: serializePublicKeyCredential(cred),
      });
      if (!finishRes.data?.ok) {
        throw new Error(finishRes.data?.error || "finish_failed");
      }
      toast.success(t("security.passkeys.registered", "Passkey enregistrée avec succès."));
      await load();
    } catch (e) {
      toast.error(
        t("security.passkeys.errors.serverError", "Erreur serveur : ") +
          (e?.response?.data?.error || e?.message || "unknown"),
      );
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div data-testid="super-admin-passkeys-page" className="max-w-4xl">
      <div className="mb-6">
        <h1 className="font-heading text-3xl text-primary flex items-center gap-3">
          <KeyRound className="h-7 w-7" />
          {t("security.passkeys.title", "Passkeys / Clés d'accès")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          {t(
            "security.passkeys.intro",
            "Enregistrez une nouvelle passkey depuis cet appareil (Windows Hello, Touch ID, clé de sécurité…) pour vous connecter à ITERCATUS. Les passkeys existantes restent actives.",
          )}
        </p>
      </div>

      <div className="bg-white border border-border rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-lg text-primary">
              {t("security.passkeys.registerHere", "Enregistrer sur cet appareil")}
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              {t(
                "security.passkeys.registerHelp",
                "Cette action déclenche l'invite du système d'exploitation. Vous devrez confirmer avec Windows Hello, Touch ID ou votre clé de sécurité.",
              )}
            </p>
          </div>
          <Button
            type="button"
            onClick={handleRegister}
            disabled={registering}
            data-testid="super-admin-passkeys-register-btn"
          >
            {registering ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            {t("security.passkeys.registerBtn", "Enregistrer une passkey sur cet appareil")}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <div className="p-6 border-b border-border">
          <h2 className="font-heading text-lg text-primary">
            {t("security.passkeys.listTitle", "Passkeys enregistrées")}
          </h2>
        </div>
        {loading ? (
          <div className="p-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {t("security.passkeys.loading", "Chargement…")}
          </div>
        ) : creds.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            {t("security.passkeys.empty", "Aucune passkey enregistrée pour ce compte.")}
          </div>
        ) : (
          <table
            className="w-full text-sm"
            data-testid="super-admin-passkeys-table"
          >
            <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-6 py-3">{t("security.passkeys.col.name", "Nom")}</th>
                <th className="text-left px-6 py-3">{t("security.passkeys.col.created", "Créée le")}</th>
                <th className="text-left px-6 py-3">{t("security.passkeys.col.lastUsed", "Dernière utilisation")}</th>
                <th className="text-left px-6 py-3">{t("security.passkeys.col.status", "Statut")}</th>
              </tr>
            </thead>
            <tbody>
              {creds.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-border"
                  data-testid={`super-admin-passkey-row-${c.id}`}
                >
                  <td className="px-6 py-3 font-medium">{c.friendly_name || "—"}</td>
                  <td className="px-6 py-3 text-muted-foreground">{formatDate(c.created_at)}</td>
                  <td className="px-6 py-3 text-muted-foreground">{formatDate(c.last_used_at)}</td>
                  <td className="px-6 py-3">
                    {c.revoked ? (
                      <span className="inline-flex items-center gap-1 text-destructive text-xs font-medium">
                        <ShieldOff className="h-4 w-4" />
                        {t("security.passkeys.status.revoked", "Révoquée")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                        <ShieldCheck className="h-4 w-4" />
                        {t("security.passkeys.status.active", "Active")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
